import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '@/config/config';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { IncompleteProduct } from './dashboardService';
import {
  getCategories as fetchCategories,
  createCategory as createCategoryService,
  Category as CategoryFromService,
} from './categoryService';
import { Category as ProductCategory } from '@/types/categories';
import { paths } from '@/lib/apiPaths';
import { logError, logDebug, logWarn } from '@/utils/errorHandler';

// PRODUCTS_PATH should be empty string to work with the backend URL structure
// The backend routes 'api/' to products.urls which registers the viewset at ''
const PRODUCTS_PATH = '/api/products'; // Add /api prefix since API_URL is now empty

// Define core fields that should trigger activity logging when changed
const CORE_FIELDS = [
  'sku',
  'name',
  'category',
  'brand',
  'tags',
  'barcode', // GTIN
  'is_active',
];

// Helper function to check for HTML responses
const isHtmlResponse = (data: any): boolean => {
  if (typeof data === 'string' && data.trim().startsWith('<!DOCTYPE html>')) {
    logError(
      'HTML response detected instead of JSON data',
      {
        component: 'productService',
        action: 'isHtmlResponse',
        metadata: { sessionExpired: true },
      },
      {
        showToast: true,
        userFacing: true,
      }
    );
    // This could trigger a re-login if needed
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Only redirect if we're not already on the login page
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?sessionExpired=true';
    }
    return true;
  }
  return false;
};

// ProductImage interface removed as part of legacy image code cleanup

export interface Product {
  id?: number;
  name: string;
  description: string;
  sku: string;
  // Category comes back from the API as a list of ancestors:
  //   []                 → no category
  //   [root, …, leaf]    → full path
  // We also still allow your legacy string‐only clients in some places,
  // so we keep string here too:
  category: ProductCategory[] | ProductCategory | string;
  category_name?: string; // Simple string category name from API response
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  is_active: boolean;

  // Additional Product Information (Optional)
  brand?: string;
  tags?: string[];
  barcode?: string;

  // Technical Specifications (Optional)
  weight?: number;
  height?: number;
  width?: number;
  length?: number;
  volume?: number;

  // Pricing & Stock Information (Optional)
  price?: number;
  prices?: ProductPrice[];
  stock?: number;
  min_stock?: number;

  // Family Reference (Optional)
  family?: ProductFamily | string | number;
  family_name?: string; // Simple string family name from API response

  // Images & Assets (Optional)
  primary_image_thumb?: string;
  primary_image_large?: string;
  primary_asset_url?: string; // URL to primary asset from API response
  assets?: ProductAsset[];

  // Attribute-related fields (Optional)
  attributes?: ProductAttribute[];
  attribute_groups?: ProductAttributeGroup[];

  // Completeness metrics (Optional)
  completeness_percent?: number;

  // Archiving status (Optional)
  is_archived?: boolean;

  // API response may include these fields
  [key: string]: any;
}

export const PRODUCTS_API_URL = `/api/products`;

// Standardized query keys for React Query
export const QUERY_KEYS = {
  PRODUCTS: 'products',
  PRODUCT: (id: number | string) => ['product', id],
  PRODUCT_ASSETS: (id: number | string) => ['assets', id],
  PRODUCT_PRICES: (id: number | string) => ['prices', id],
  CATEGORIES: 'categories',
};

export interface ProductAttribute {
  id: number;
  name: string;
  value?: string;
  group?: string;
  locale?: string;
  updated_at?: string;
  isMandatory?: boolean;
  // Properties for attribute group structure
  order?: number;
  items?: any[];
  // Properties for attribute items
  attribute?: number;
  attribute_label?: string;
  attribute_code?: string;
  attribute_type?: string;
  channel?: string;
  value_id?: number;
}

export interface ProductAsset {
  id: number;
  name: string;
  type: string;
  asset_type?: string; // Added for compatibility with backend responses
  url: string; // For frontend display (derived from file or file_url)
  file?: string; // Actual backend field
  file_url?: string; // Pre-signed URL from backend (S3 with query parameters)
  // Three-size URLs for optimized image display
  url_thumbnail?: string;
  url_medium?: string;
  url_full?: string;
  size: string;
  resolution?: string;
  uploaded_by: string;
  uploaded_at: string;
  is_primary?: boolean;
  is_archived?: boolean;
  parent_asset_id?: number;
  version?: string;
  order?: number;
  archived?: boolean;
  tags?: string[];
  content_type?: string;
}

export interface ProductVersion {
  id: number;
  version: string;
  timestamp: string;
  user: string;
  summary: string;
}

export interface PriceHistory {
  date: string;
  oldPrice: string;
  newPrice: string;
  user: string;
}

// Type for explicit product relations
export interface ProductRelation {
  id: number;
  product_id: number;
  related_product_id: number;
  relationship_type: RelationshipType;
  is_pinned: boolean;
  created_at: string;
  created_by?: string;
  source: 'manual' | 'algorithm';
  notes?: string;
}

export type RelationshipType =
  | 'accessory'
  | 'variant'
  | 'frequently_bought_together'
  | 'replacement'
  | 'similar'
  | 'general';

// Fetch available attributes for an attribute set (by setId)
const getAttributeSet = async (setId: number) => {
  // Example endpoint: /api/attribute-sets/:setId/
  const url = `/api/attribute-sets/${setId}/`;
  const response = await axiosInstance.get(url);
  // Should return { setId, attributes: [...] }
  return response.data;
};

// Fetch all attribute values for a product
const getAttributeValues = async (productId: number) => {
  // Example endpoint: /api/products/:id/attributes/
  const url = `${PRODUCTS_API_URL}/${productId}/attributes/`;
  const response = await axiosInstance.get(url);
  return response.data;
};

// Update or add an attribute value for a product
const updateAttributeValue = async (
  productId: number,
  attributeId: number,
  payload: { value: any; locale: string }
) => {
  // PATCH or POST to /api/products/:id/attributes/:attributeId/
  const url = `${PRODUCTS_API_URL}/${productId}/attributes/${attributeId}/`;
  // Use PATCH for update, POST for add (here we use PATCH for both for idempotency)
  const response = await axiosInstance.patch(url, payload);
  return response.data;
};

// Create a new attribute value for a product
const createAttributeValue = async (
  productId: number,
  attributeId: number,
  payload: { value: any; locale: string }
) => {
  // POST to /api/products/:id/attributes/:attributeId/
  const url = `${PRODUCTS_API_URL}/${productId}/attributes/${attributeId}/`;
  const response = await axiosInstance.post(url, payload);
  return response.data;
};

// Log attribute activity (optional, if backend supports)
const logAttributeActivity = async (payload: {
  action: string;
  userId: number | string | undefined;
  productId: number;
  attributeId: number;
}) => {
  // Example endpoint: /api/attribute-activities/
  const url = `/api/attribute-activities/`;
  await axiosInstance.post(url, payload);
};

// Create a new attribute in an attribute set
const createAttribute = async (
  attributeSetId: number,
  attributeData: {
    name: string;
    groupId?: number;
    groupName: string;
    dataType: string;
    unit?: string;
    isMandatory: boolean;
    options?: Array<{ value: string; label: string }>;
    validationRule?: string;
  }
) => {
  const url = `/api/attribute-sets/${attributeSetId}/attributes/`;
  const response = await axiosInstance.post(url, attributeData);
  return response.data;
};

// Add type for category options used by react-select
interface CategoryOption {
  label: string;
  value: number | string;
}

export interface ProductEvent {
  id: number;
  event_type: string;
  summary: string;
  payload: Record<string, any>;
  created_at: string; // ISO
  created_by_name: string;
  created_by?: number; // User ID for filtering
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Get product completeness data from backend
 * @param productId The ID of the product
 * @param organizationId The organization ID (may be needed depending on backend implementation)
 */
const getProductCompleteness = async (
  productId: number,
  organizationId?: string | number
): Promise<IncompleteProduct> => {
  try {
    // Option 2: Using a dedicated endpoint (preferred if available)
    const endpoint = `${PRODUCTS_PATH}/${productId}/completeness/`;
    const response = await axiosInstance.get(endpoint);
    return response.data;
  } catch {
    // If dedicated endpoint fails, try the generic endpoint with productId parameter
    try {
      const params = new URLSearchParams();
      if (organizationId) {
        params.append('organization_id', organizationId.toString());
      }
      params.append('product_id', productId.toString());

      const endpoint = `${PRODUCTS_PATH}/incomplete-products/?${params.toString()}`;
      const response = await axiosInstance.get(endpoint);

      // Handle array response (in case the API returns an array of one item)
      const data =
        Array.isArray(response.data) && response.data.length === 1
          ? response.data[0]
          : response.data;

      return data;
    } catch (secondError) {
      logError(
        'Failed to fetch product completeness data',
        {
          component: 'productService',
          action: 'getProductCompleteness',
          metadata: { productId, organizationId, error: secondError },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      throw secondError;
    }
  }
};

// Define the ProductPrice interface
export interface ProductPrice {
  id: number;
  price_type: string; // Changed from union type to any string to allow dynamic price types
  price_type_display: string;
  channel: {
    id: number;
    code: string;
    name: string;
    description: string | null;
    is_active: boolean;
  } | null;
  channel_id?: number;
  currency: string;
  amount: number;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
}

// --- Price API payload shapes -----------------------------------------
export type AddPricePayload = {
  price_type: string | number; // code or id
  currency: string; // ISO code, e.g. "USD"
  amount: number; // decimal value
  channel_id?: number | null; // optional FK to sales channel
  valid_from: string; // ISO date (YYYY-MM-DD)
  valid_to?: string | null; // optional ISO date
};

export type UpdatePricePayload = Partial<AddPricePayload>;

export interface AssetBundle {
  id: number;
  name: string;
  asset_ids: number[];
  created_at: string;
}

// Helper to get content_type from File or ProductAsset
function getContentType(fileOrAsset: File | ProductAsset): string {
  if (fileOrAsset instanceof File) return fileOrAsset.type || '';
  return fileOrAsset.content_type || fileOrAsset.type || '';
}

// Product family interfaces
export interface ProductFamily {
  id: number;
  name?: string;
  code?: string;
  label?: string;
  description?: string;
}

// Product attribute group interface
export interface ProductAttributeGroup {
  id: number;
  name: string;
  description?: string;
  items?: ProductAttribute[];
}

export interface EffectiveAttributeGroup {
  groupId: number;
  groupName: string;
  required: boolean;
  order: number;
  attributes: EffectiveAttribute[];
}

export interface EffectiveAttribute {
  id: number;
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
  value: any;
  value_id: number | null; // Add AttributeValue ID for editing existing values
  locale: string | null;
  channel: string | null;
  overridden: boolean;
  has_record: boolean; // True if AttributeValue record exists, regardless of content
  is_required: boolean;
  // Attribute configuration fields for proper frontend handling
  default_unit?: string | null;
  allowed_units?: string[];
  default_currency?: string | null;
  validation_rule?: string | null;
  allowed_html_tags?: string[];
  allowed_file_types?: string[];
  max_file_size?: number | null;
}

// Comment-related interfaces
export interface Comment {
  id: number;
  body: string;
  author: number;
  author_name: string;
  author_full_name: string;
  parent: number | null;
  is_reply: boolean;
  thread_depth: number;
  replies_count: number;
  created_at: string;
  updated_at: string;
  replies?: Comment[]; // For nested comments in list view
}

export interface CreateCommentPayload {
  body: string;
  parent?: number | null;
}

export interface UpdateCommentPayload {
  body: string;
}

export interface CommentListResponse {
  results: Comment[];
  count: number;
  next: string | null;
  previous: string | null;
  total_comments: number;
  top_level_comments: number;
}

// Helper function to inject organization defaults into price payloads
const injectOrganizationDefaults = async (priceData: AddPricePayload): Promise<AddPricePayload> => {
  // If currency or price_type are missing, try to get organization defaults
  if (!priceData.currency || !priceData.price_type) {
    try {
      // Get organization settings (this would need to be imported from a hook or service)
      // For now, we'll just return the original data since we can't easily access hooks here
      // The backend will handle the injection as the primary mechanism
      logDebug('Price data missing currency or price_type, backend will inject defaults', {
        priceData,
      });
    } catch (error) {
      logWarn('Could not fetch organization defaults for price injection', {
        component: 'productService',
        action: 'injectOrganizationDefaults',
        metadata: { error, priceData },
      });
    }
  }
  return priceData;
};

// Helper function for update operations - only inject if fields are explicitly null/empty
const injectOrganizationDefaultsForUpdate = async (
  priceData: Partial<ProductPrice>
): Promise<Partial<ProductPrice>> => {
  // For updates, we only inject defaults if fields are explicitly being cleared
  if (priceData.currency === '' || priceData.currency === null) {
    // Currency cleared, backend will inject default
  }
  // Note: price_type field would be handled similarly but it's not in ProductPrice interface
  return priceData;
};

// Main product service object
export const productService = {
  // Get all products with optional pagination support
  getProducts: async (
    filters: Record<string, any> = {},
    fetchAll: boolean = true,
    includeAssets: boolean = true
  ): Promise<Product[] | PaginatedResponse<Product>> => {
    /* ------------------------------------------------------------------ *
     * 1️⃣ build the first-page URL with page & page_size if provided
     * ------------------------------------------------------------------ */
    const qs = new URLSearchParams();

    // Add all filters as query parameters
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        // Handle arrays (like tags)
        if (Array.isArray(value)) {
          // Only add non-empty arrays
          if (value.length > 0) {
            qs.append(key, value.join(','));
          }
        } else {
          qs.append(key, String(value));
        }
      }
    }

    // Ensure these critical filters are properly applied
    if (!qs.has('page') && filters.page) qs.append('page', String(filters.page));
    if (!qs.has('page_size') && filters.page_size)
      qs.append('page_size', String(filters.page_size));
    if (!qs.has('search') && filters.search) qs.append('search', String(filters.search));
    if (!qs.has('category') && filters.category && filters.category !== 'all')
      qs.append('category', String(filters.category));
    if (!qs.has('brand') && filters.brand) qs.append('brand', String(filters.brand));
    if (!qs.has('is_active') && filters.status && filters.status !== 'all') {
      qs.append('is_active', filters.status === 'active' ? 'true' : 'false');
    }

    let nextPageUrl: string | null = qs.toString().length
      ? `${PRODUCTS_PATH}/?${qs.toString()}`
      : `${PRODUCTS_PATH}/`;

    let allProducts: Product[] = [];

    try {
      /* ------------------------------------------------------------------ *
       * 2️⃣ fetch one page (or all pages if fetchAll=true)
       * ------------------------------------------------------------------ */
      while (nextPageUrl && (fetchAll || allProducts.length === 0)) {
        const response: AxiosResponse<any> = await axiosInstance.get(nextPageUrl);

        // Check for HTML response
        if (isHtmlResponse(response.data)) {
          logError(
            'Received HTML response instead of JSON in getProducts',
            {
              component: 'productService',
              action: 'getProducts',
              metadata: { sessionExpired: true },
            },
            { showToast: false, userFacing: false }
          );
          return allProducts;
        }

        // Handle DRF browsable API response format
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // If it contains a "products" URL property instead of actual data
          if (response.data.products && allProducts.length === 0) {
            // Make a second request to get the actual products list
            const productsResponse: AxiosResponse<any> = await axiosInstance.get(
              response.data.products
            );

            // Handle paginated response from second request
            if (productsResponse.data && 'results' in productsResponse.data) {
              if (!fetchAll) {
                // For single page request, return paginated response
                return productsResponse.data;
              }
              allProducts = [...allProducts, ...productsResponse.data.results];
              nextPageUrl = productsResponse.data.next;
              continue;
            } else if (Array.isArray(productsResponse.data)) {
              // If it's an array, we have all products
              return productsResponse.data;
            } else {
              logError(
                'Unexpected response format from products URL',
                {
                  component: 'productService',
                  action: 'getProducts',
                  metadata: { responseType: typeof productsResponse.data },
                },
                { showToast: false, userFacing: false }
              );
              return allProducts;
            }
          }

          // Handle paginated response format
          if ('results' in response.data) {
            // If the caller only wants a single page, immediately return the
            // untouched paginated payload so that consumers (e.g. ProductsTable)
            // can rely on `count`, `next`, and `previous` metadata.  This fixes
            // pagination controls that were guessing due to missing data.
            if (!fetchAll) {
              return response.data;
            }
            // For fetch all, accumulate results
            allProducts = [...allProducts, ...response.data.results];
            nextPageUrl = response.data.next;
            continue;
          }
        }

        // If we get an array directly, return it
        if (Array.isArray(response.data)) {
          return response.data;
        }

        // Break the loop if we've processed this page but couldn't determine the next page
        break;
      }

      // Enrich with assets only when the caller asks for it
      if (includeAssets) {
        for (const product of allProducts) {
          try {
            if (!product.id) continue;
            const assets = await productService.getProductAssets(product.id);
            product.assets = assets;

            // Find primary asset or first image and set product image URLs
            if (Array.isArray(assets) && assets.length > 0) {
              const primaryAsset =
                assets.find(
                  a =>
                    a.is_primary && (a.type || a.asset_type || '').toLowerCase().includes('image')
                ) ||
                assets.find(a => (a.type || a.asset_type || '').toLowerCase().includes('image'));

              // Set primary image URLs if primary asset found
              if (primaryAsset) {
                product.primary_image_thumb = primaryAsset.url_thumbnail || primaryAsset.url;
                product.primary_image_large = primaryAsset.url_full || primaryAsset.url;
                product.primary_asset_url = primaryAsset.url;
              }
            }
          } catch (assetErr) {
            logError(
              'Failed to load assets for product',
              {
                component: 'productService',
                action: 'getProducts',
                metadata: { productId: product.id, error: assetErr },
              },
              {
                showToast: false,
                userFacing: false,
              }
            );
          }
        }
      }

      return allProducts;
    } catch (error) {
      logError(
        'Failed to fetch products',
        {
          component: 'productService',
          action: 'getProducts',
          metadata: {
            error: error,
            status: axios.isAxiosError(error) ? error.response?.status : undefined,
            responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      return allProducts; // Return what we have so far instead of an empty array
    }
  },

  // Get a single product
  getProduct: async (id: number): Promise<Product> => {
    const url = `${PRODUCTS_PATH}/${id}/`;
    const response = await axiosInstance.get(url);
    const product = response.data;

    // Ensure the product has prices array (fetch separately if not included)
    if (!product.prices || product.prices.length === 0) {
      try {
        product.prices = await productService.getPrices(id);
      } catch (error) {
        logError(
          'Failed to fetch prices for product',
          {
            component: 'productService',
            action: 'getProduct',
            metadata: { productId: id, error: error },
          },
          {
            showToast: false,
            userFacing: false,
          }
        );
        product.prices = [];
      }
    }

    // Handle category data structure
    if (typeof product.category === 'string' && product.category !== '') {
      product.category = { id: 0, name: product.category };
    }

    // Ensure family data is complete
    if (product.family) {
      try {
        // If family is just an ID, fetch the full family data
        if (
          typeof product.family === 'number' ||
          (typeof product.family === 'object' && !product.family.code && !product.family.label)
        ) {
          const familyId = typeof product.family === 'number' ? product.family : product.family.id;

          // Fetch the complete family data
          if (familyId) {
            const familyResponse = await axiosInstance.get(`/api/families/${familyId}/`);
            if (familyResponse.data) {
              product.family = familyResponse.data;
              // Store family name in a separate field for convenience
              product.family_name = familyResponse.data.label || familyResponse.data.name;
            }
          }
        }
      } catch (error) {
        logError(
          'Failed to fetch family details for product',
          {
            component: 'productService',
            action: 'getProduct',
            metadata: { productId: id, familyId: product.family, error: error },
          },
          {
            showToast: false,
            userFacing: false,
          }
        );
        // Keep the original family data if fetching fails
      }
    }

    return product;
  },

  // Create a new product
  createProduct: async (
    product: Omit<Product, 'id' | 'created_by' | 'created_at' | 'updated_at'> | FormData
  ): Promise<Product> => {
    const url = API_ENDPOINTS.products.create;
    try {
      // Handle FormData or regular object
      let response;
      if (product instanceof FormData) {
        // Explicitly set the correct headers for FormData with files
        response = await axiosInstance.post(url, product, {
          headers: {
            'Content-Type': 'multipart/form-data',
            // Remove any preset content-type that might interfere
            Accept: 'application/json',
          },
        });
      } else {
        const productData = { ...product } as any; // Use type assertion to handle property assignments

        // Handle tags special case - if it's an array, stringify it
        if (Array.isArray(productData.tags)) {
          productData.tags = JSON.stringify(productData.tags);
        }

        response = await axiosInstance.post(url, productData);
      }
      return response.data;
    } catch (error) {
      logError(
        'Failed to create product',
        {
          component: 'productService',
          action: 'createProduct',
          metadata: {
            isFormData: product instanceof FormData,
            error: error,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      throw error;
    }
  },

  // Update a product
  updateProduct: async (id: number, product: Partial<Product> | FormData): Promise<Product> => {
    const url = `${PRODUCTS_PATH}/${id}/`;

    // For regular objects, check if core fields are being modified
    if (!(product instanceof FormData)) {
      const hasCoreFieldChanges = Object.keys(product).some(key => CORE_FIELDS.includes(key));

      // If core fields are changed, we'll need to add activity tracking
      if (hasCoreFieldChanges) {
        // Add a flag to tell the backend to log this as a PRODUCT_CORE_EDIT activity
        // This is handled by the backend to create an activity log entry
        const productWithFlag = {
          ...product,
          _activity_type: 'PRODUCT_CORE_EDIT',
        };

        const response = await axiosInstance.patch(url, productWithFlag);
        return response.data;
      }
    }

    if (product instanceof FormData) {
      // For FormData, we need to set the correct Content-Type
      // Check if we're updating core fields
      const hasCoreFieldUpdate = Array.from(product.keys()).some(key =>
        CORE_FIELDS.includes(key as string)
      );

      if (hasCoreFieldUpdate) {
        // Add activity flag
        product.append('_activity_type', 'PRODUCT_CORE_EDIT');
      }

      const response = await axiosInstance.patch(url, product, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }

    // For regular objects with no core field changes
    const response = await axiosInstance.patch(url, product);
    return response.data;
  },

  // Delete a product
  deleteProduct: async (id: number): Promise<void> => {
    const url = `${PRODUCTS_PATH}/${id}/`;
    await axiosInstance.delete(url);
  },

  // Get all categories (Delegating to categoryService)
  getCategories: async (): Promise<CategoryFromService[]> => {
    try {
      return await fetchCategories();
    } catch (error) {
      logError(
        'Failed to fetch categories',
        {
          component: 'productService',
          action: 'getCategories',
          metadata: { error: error },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      // Return empty array as fallback
      return [];
    }
  },

  // Search categories (Should be updated to use categoryService's search or filter mechanism)
  searchCategories: async (inputValue: string): Promise<CategoryOption[]> => {
    try {
      // Fetch all categories from the categoryService
      const categories = await fetchCategories();

      // Filter categories based on input value
      return categories
        .filter(category => category.name.toLowerCase().includes(inputValue.toLowerCase()))
        .map(category => ({
          label: category.name,
          value: category.id,
        }));
    } catch (error) {
      logError(
        'Failed to search categories',
        {
          component: 'productService',
          action: 'searchCategories',
          metadata: { inputValue, error },
        },
        { showToast: false, userFacing: false }
      );
      return []; // Return empty array as fallback
    }
  },

  // Create a new category (Delegating to categoryService)
  createCategory: async (data: {
    name: string;
    parentId?: number;
  }): Promise<CategoryFromService> => {
    try {
      return await createCategoryService(data.name, data.parentId);
    } catch (error) {
      logError(
        'Failed to create category',
        {
          component: 'productService',
          action: 'createCategory',
          metadata: { categoryName: data.name, parentId: data.parentId, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error; // Propagate error to be handled by the caller
    }
  },

  // Search tags - Updated to use real API
  searchTags: async (inputValue: string): Promise<{ label: string; value: string }[]> => {
    try {
      // Call API endpoint for tags
      const response = await axiosInstance.get(`${PRODUCTS_PATH}/tags/`, {
        params: inputValue ? { search: inputValue } : {},
      });

      // Handle both paginated and direct array responses
      let tags: any;

      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        // Handle paginated response
        tags = response.data.results;
      } else {
        // Handle direct array response
        tags = response.data;
      }

      if (Array.isArray(tags)) {
        // The backend returns an array of strings (tag names)
        const transformedTags = tags.map(tagName => ({
          label: String(tagName),
          value: String(tagName),
        }));

        return transformedTags;
      } else {
        logWarn('searchTags API returned non-array response', {
          component: 'productService',
          action: 'searchTags',
          metadata: { responseType: typeof tags, inputValue },
        });
        return [];
      }
    } catch (error) {
      logError(
        'Failed to search tags',
        {
          component: 'productService',
          action: 'searchTags',
          metadata: {
            inputValue,
            error,
            status: axios.isAxiosError(error) ? error.response?.status : undefined,
            responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
          },
        },
        { showToast: false, userFacing: false }
      );
      // Return empty array as fallback
      return [];
    }
  },

  // Create a new tag - Updated to use real API
  createTag: async (data: { name: string }): Promise<{ id: string; name: string }> => {
    try {
      // Call API to create tag
      const response = await axiosInstance.post(`${PRODUCTS_PATH}/tags/`, data);

      // The backend returns just the tag name as a string
      const tagName = response.data;

      return {
        id: tagName, // Use the tag name as the ID
        name: tagName,
      };
    } catch (error) {
      logError(
        'Failed to create tag',
        {
          component: 'productService',
          action: 'createTag',
          metadata: { tagName: data.name, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error; // Propagate error to be handled by the caller
    }
  },

  // Search brands - Similar to searchTags but for brands
  searchBrands: async (inputValue: string = ''): Promise<{ label: string; value: string }[]> => {
    try {
      // Call API endpoint for brands
      const response = await axiosInstance.get(`${PRODUCTS_PATH}/brands/`, {
        params: inputValue ? { search: inputValue } : {},
      });

      // Handle both paginated and direct array responses
      let brands: any;

      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        // Handle paginated response
        brands = response.data.results;
      } else {
        // Handle direct array response
        brands = response.data;
      }

      if (Array.isArray(brands)) {
        // The backend returns an array of strings (brand names)
        const transformedBrands = brands.map(brandName => ({
          label: String(brandName),
          value: String(brandName),
        }));

        return transformedBrands;
      } else {
        logWarn('searchBrands API returned non-array response', {
          component: 'productService',
          action: 'searchBrands',
          metadata: { responseType: typeof brands, inputValue },
        });
        return [];
      }
    } catch (error) {
      logError(
        'Failed to search brands',
        {
          component: 'productService',
          action: 'searchBrands',
          metadata: {
            inputValue,
            error,
            status: axios.isAxiosError(error) ? error.response?.status : undefined,
            responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
          },
        },
        { showToast: false, userFacing: false }
      );
      // Return empty array as fallback
      return [];
    }
  },

  // Get product statistics
  getStats: async (): Promise<{
    total_products: number;
    total_value: number;
  }> => {
    const url = `${PRODUCTS_PATH}/stats/`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  // Get product attributes
  getProductAttributes: async (productId: number): Promise<ProductAttribute[]> => {
    try {
      // Use more consistent URL path
      const url = `${PRODUCTS_API_URL}/${productId}/attributes/`;

      const response = await axiosInstance.get(url);

      // Check for HTML response (which would indicate an error)
      if (isHtmlResponse(response.data)) {
        logError(
          'Received HTML response instead of JSON in getProductAttributes',
          {
            component: 'productService',
            action: 'getProductAttributes',
            metadata: { productId, sessionExpired: true },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // Handle different response formats
      let attributes: ProductAttribute[] = [];

      if (Array.isArray(response.data)) {
        attributes = response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        attributes = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        // If response is an object with attribute data, transform it to an array
        attributes = Object.entries(response.data).map(([key, value]) => {
          // Handle if value is a string
          if (typeof value === 'string') {
            return {
              id: Math.random(), // Generate a unique ID
              name: key,
              value: value,
              group: 'General',
              locale: 'en',
              updated_at: new Date().toISOString(),
              isMandatory: false,
            } as ProductAttribute;
          }

          // Handle if value is an object
          const attrValue = value as any;
          return {
            id: attrValue.id || Math.random(),
            name: attrValue.name || key,
            value: attrValue.value || '',
            group: attrValue.group || 'General',
            locale: attrValue.locale || 'en',
            updated_at: attrValue.updated_at || new Date().toISOString(),
            isMandatory: !!attrValue.isMandatory,
          } as ProductAttribute;
        });
      } else {
        logError(
          'Unexpected response format in getProductAttributes',
          {
            component: 'productService',
            action: 'getProductAttributes',
            metadata: {
              productId,
              responseType: typeof response.data,
              responseSample:
                typeof response.data === 'object'
                  ? JSON.stringify(response.data).substring(0, 100)
                  : response.data,
            },
          },
          { showToast: false, userFacing: false }
        );

        // Create some mock attributes for debugging if real ones aren't available
        if (process.env.NODE_ENV === 'development') {
          return [
            {
              id: 1,
              name: 'Weight',
              value: '2.5 kg',
              group: 'Physical',
              locale: 'en',
              updated_at: new Date().toISOString(),
              isMandatory: true,
            },
            {
              id: 2,
              name: 'Color',
              value: 'Red',
              group: 'Appearance',
              locale: 'en',
              updated_at: new Date().toISOString(),
              isMandatory: false,
            },
          ];
        }
        return [];
      }

      // Do some basic validation of the attributes
      for (const attr of attributes) {
        if (!attr.group) attr.group = 'General';
        if (!attr.locale) attr.locale = 'en';
        if (attr.isMandatory === undefined) attr.isMandatory = false;
        if (!attr.updated_at) attr.updated_at = new Date().toISOString();
      }

      return attributes;
    } catch (error) {
      logError(
        'Failed to fetch product attributes',
        {
          component: 'productService',
          action: 'getProductAttributes',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );
      // For development, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        return [
          {
            id: 1,
            name: 'Width',
            value: '30 cm',
            group: 'Dimensions',
            locale: 'en',
            updated_at: new Date().toISOString(),
            isMandatory: true,
          },
          {
            id: 2,
            name: 'Material',
            value: 'Aluminum',
            group: 'Specifications',
            locale: 'en',
            updated_at: new Date().toISOString(),
            isMandatory: false,
          },
        ];
      }
      return [];
    }
  },

  // Get product attribute groups with on-demand loading
  getProductAttributeGroupsOnDemand: async (
    productId: number,
    options: { locale?: string; channel?: string } = {}
  ): Promise<ProductAttributeGroup[]> => {
    try {
      // Use the new effective attributes endpoint instead of legacy attribute-groups
      const effectiveGroups = await productService.getEffectiveAttributes(
        productId,
        options.locale,
        options.channel
      );

      // Transform to legacy format for backward compatibility
      return effectiveGroups.map(group => ({
        id: group.groupId,
        name: group.groupName,
        description: `Group with ${group.attributes.length} attributes`,
        items: group.attributes.map(attr => ({
          id: attr.value_id || attr.id,
          name: attr.label,
          attribute: attr.id,
          value: attr.value,
          value_id: attr.value_id || undefined,
          locale: attr.locale || undefined,
          channel: attr.channel || undefined,
          // Additional fields for compatibility
          attribute_label: attr.label,
          attribute_code: attr.code,
          attribute_type: attr.data_type,
        })),
      }));
    } catch (error) {
      logError(
        'Failed to fetch product attribute groups on demand',
        {
          component: 'productService',
          action: 'getProductAttributeGroupsOnDemand',
          metadata: { productId, options, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Get product assets
  getProductAssets: async (productId: number): Promise<ProductAsset[]> => {
    const url = `${PRODUCTS_API_URL}/${productId}/assets/`;

    try {
      const response = await axiosInstance.get(url);

      if (isHtmlResponse(response.data)) {
        logError(
          'Received HTML response instead of JSON in getProductAssets',
          {
            component: 'productService',
            action: 'getProductAssets',
            metadata: { productId, sessionExpired: true },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // Handle different response formats
      let data: any[] = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        data = response.data.results;
      } else {
        logError(
          'Unexpected response format in getProductAssets',
          {
            component: 'productService',
            action: 'getProductAssets',
            metadata: {
              productId,
              responseType: typeof response.data,
              responseSample:
                typeof response.data === 'object'
                  ? JSON.stringify(response.data).substring(0, 100)
                  : response.data,
            },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // Transform the data to match our expected format
      const assets = data.map((asset: any) => {
        // Smart URL handling for both S3 (production) and local (development)
        const rawUrl = asset.file_url || asset.file || asset.url || '';

        // Determine the display URL:
        // - For S3 pre-signed URLs (with query params), use directly
        // - For local/relative URLs, they'll be processed by getImageUrlFromAsset()
        const displayUrl = rawUrl;

        return {
          id: asset.id,
          name: asset.name || (asset.file ? asset.file.split('/').pop() : 'Unknown'),
          type: asset.asset_type || asset.type || 'image',
          asset_type: asset.asset_type || asset.type || 'image', // Add both fields for consistency
          // Store raw URL - will be processed by getImageUrlFromAsset() when used
          url: displayUrl,
          file: asset.file,
          file_url: asset.file_url, // Store file_url separately for reference
          // Include three-size URLs for optimized image display
          url_thumbnail: asset.url_thumbnail || asset.thumbnail_url,
          url_medium: asset.url_medium || asset.medium_url,
          url_full: asset.url_full || asset.full_url,
          size: asset.file_size || '0', // Ensure size is always a string for consistent handling
          uploaded_by: asset.uploaded_by_name || asset.uploaded_by || 'System',
          uploaded_at: asset.uploaded_at || new Date().toISOString(),
          is_primary: !!asset.is_primary, // Ensure boolean type
          order: asset.order || 0,
          archived: asset.archived || false,
          tags: asset.tags || [], // Include tags with empty array fallback
          content_type: getContentType(asset),
        };
      });

      // Save the fetched assets to localStorage for caching
      try {
        localStorage.setItem(`product_assets_${productId}`, JSON.stringify(assets));
      } catch (cacheError) {
        logWarn('Failed to cache assets to localStorage', {
          component: 'productService',
          action: 'getProductAssets',
          metadata: { productId, cacheError },
        });
      }

      return assets;
    } catch (error) {
      logError(
        'Failed to fetch product assets',
        {
          component: 'productService',
          action: 'getProductAssets',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );

      // Try to get cached assets from localStorage if API fails
      try {
        const cachedAssetsJSON = localStorage.getItem(`product_assets_${productId}`);
        if (cachedAssetsJSON) {
          const cachedAssets = JSON.parse(cachedAssetsJSON);

          // Ensure URL compatibility for cached assets
          return cachedAssets.map((asset: any) => {
            if (!asset.url && asset.file) {
              asset.url = asset.file;
            }
            return asset;
          });
        }
      } catch (cacheError) {
        logError(
          'Failed to read cached assets from localStorage',
          {
            component: 'productService',
            action: 'getProductAssets',
            metadata: { productId, cacheError },
          },
          { showToast: false, userFacing: false }
        );
      }

      return [];
    }
  },

  // Get product versions
  getProductVersions: async (productId: number): Promise<ProductVersion[]> => {
    try {
      const url = `/api/products/${productId}/versions/`;

      const response = await axiosInstance.get(url);

      // Check if response is HTML first
      if (typeof response.data === 'string' && response.data.startsWith('<!DOCTYPE')) {
        logError(
          'Received HTML response instead of JSON in getProductVersions',
          {
            component: 'productService',
            action: 'getProductVersions',
            metadata: { productId, sessionExpired: true },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // Check if response is an array
      if (!Array.isArray(response.data)) {
        logError(
          'Invalid response format for product versions',
          {
            component: 'productService',
            action: 'getProductVersions',
            metadata: {
              productId,
              responseType: typeof response.data,
              isHTML: typeof response.data === 'string' && response.data.startsWith('<!DOCTYPE'),
            },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with detailed information
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorData = error.response?.data;

        logError(
          'Server error in getProductVersions',
          {
            component: 'productService',
            action: 'getProductVersions',
            metadata: {
              productId,
              status,
              message: error.message,
              url: `/api/products/${productId}/versions/`,
              response: errorData
                ? typeof errorData === 'string'
                  ? errorData.substring(0, 200) + '...'
                  : errorData
                : 'No response data',
            },
          },
          { showToast: false, userFacing: false }
        );

        // Log a warning that an "AttributeError" is typically a backend code issue
        if (typeof errorData === 'string' && errorData.includes('AttributeError')) {
          logWarn('AttributeError detected in getProductVersions - backend issue', {
            component: 'productService',
            action: 'getProductVersions',
            metadata: { productId, errorType: 'AttributeError' },
          });
        }

        // For 500 errors specifically, we'll log even more details to help debug
        if (status === 500) {
          logWarn('Returning empty array due to server error 500 in getProductVersions', {
            component: 'productService',
            action: 'getProductVersions',
            metadata: { productId, status },
          });
        }
      } else {
        logError(
          'Failed to fetch product versions',
          {
            component: 'productService',
            action: 'getProductVersions',
            metadata: { productId, error },
          },
          { showToast: false, userFacing: false }
        );
      }

      // Always return an empty array when there's an error to prevent UI crashes
      return [];
    }
  },

  // Get product price history
  getPriceHistory: async (productId: number): Promise<PriceHistory[]> => {
    try {
      const url = `/api/products/${productId}/price-history/`;

      const response = await axiosInstance.get(url);

      // Check if response is HTML first
      if (typeof response.data === 'string' && response.data.startsWith('<!DOCTYPE')) {
        logError(
          'Received HTML response instead of JSON when fetching price history',
          {
            component: 'productService',
            action: 'getPriceHistory',
            metadata: { productId },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // Check if response is an array
      if (!Array.isArray(response.data)) {
        logError(
          'Invalid response format for price history',
          {
            component: 'productService',
            action: 'getPriceHistory',
            metadata: {
              productId,
              responseType: typeof response.data,
              isHtml: typeof response.data === 'string' && response.data.startsWith('<!DOCTYPE'),
            },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      return response.data;
    } catch (error) {
      logError(
        'Failed to fetch price history',
        {
          component: 'productService',
          action: 'getPriceHistory',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Get related products
  getRelatedProducts: async (productId: number): Promise<Product[]> => {
    try {
      // Use fields parameter to include asset and image fields
      const url = `${PRODUCTS_API_URL}/${productId}/related-list/?fields=id,name,sku,price,currency,is_active,primary_image_thumb,primary_asset_url,brand,category_name,assets`;

      const response = await axiosInstance.get(url);

      // Simple validation - if it's not an array, log error and return empty array
      if (!Array.isArray(response.data)) {
        logError(
          'Invalid response format for related products',
          {
            component: 'productService',
            action: 'getRelatedProducts',
            metadata: { productId, responseType: typeof response.data },
          },
          { showToast: false, userFacing: false }
        );
        return [];
      }

      // For each product, ensure we have asset data
      const productsWithAssets = await Promise.all(
        response.data.map(async (product: any) => {
          // If product doesn't have assets, try to fetch them
          if (!product.assets || product.assets.length === 0) {
            try {
              const assets = await productService.getProductAssets(product.id);
              product.assets = assets;
            } catch (error) {
              logWarn(`Failed to fetch assets for related product ${product.id}`, {
                component: 'productService',
                action: 'getRelatedProducts',
                metadata: { productId, relatedProductId: product.id, error },
              });
              product.assets = [];
            }
          }
          return product;
        })
      );

      return productsWithAssets;
    } catch (error) {
      logError(
        'Failed to fetch related products',
        {
          component: 'productService',
          action: 'getRelatedProducts',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Add or update related product relationship
  toggleRelatedProduct: async (
    productId: number,
    relatedProductId: number,
    isPinned: boolean,
    relationshipType: RelationshipType = 'general',
    notes: string = ''
  ): Promise<boolean> => {
    try {
      const url = `${PRODUCTS_API_URL}/${productId}/related-add/`;

      await axiosInstance.post(url, {
        related_product_id: relatedProductId,
        is_pinned: isPinned,
        relationship_type: relationshipType,
        notes: notes,
      });

      return true;
    } catch (error) {
      // If it's already related and we're trying to update the status,
      // we should use PATCH instead
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.error?.includes('already exists')
      ) {
        try {
          // Try updating the existing relationship
          const updateUrl = `${PRODUCTS_API_URL}/${productId}/related/${relatedProductId}/`;
          await axiosInstance.patch(updateUrl, {
            is_pinned: isPinned,
            relationship_type: relationshipType,
            notes: notes,
          });
          return true;
        } catch (updateError) {
          logError(
            'Failed to update related product',
            {
              component: 'productService',
              action: 'toggleRelatedProduct',
              metadata: { productId, relatedProductId, updateError },
            },
            { showToast: false, userFacing: false }
          );
          return false;
        }
      }

      logError(
        'Failed to add related product',
        {
          component: 'productService',
          action: 'toggleRelatedProduct',
          metadata: { productId, relatedProductId, error },
        },
        { showToast: false, userFacing: false }
      );
      return false;
    }
  },

  // Remove related product
  removeRelatedProduct: async (productId: number, relatedProductId: number): Promise<boolean> => {
    if (!productId || !relatedProductId) {
      logError(
        'Invalid product IDs for removing relation',
        {
          component: 'productService',
          action: 'removeRelatedProduct',
          metadata: { productId, relatedProductId },
        },
        { showToast: false, userFacing: false }
      );
      return false;
    }

    try {
      // Keep the original "related" path since the backend still uses this URL pattern for DELETE
      const url = `${PRODUCTS_API_URL}/${productId}/related/${relatedProductId}/`;
      await axiosInstance.delete(url);
      return true;
    } catch (error) {
      logError(
        'Failed to remove related product',
        {
          component: 'productService',
          action: 'removeRelatedProduct',
          metadata: { productId, relatedProductId, error },
        },
        { showToast: false, userFacing: false }
      );
      return false;
    }
  },

  // Search products with debouncing support
  searchProducts: async (query: string, limit: number = 10): Promise<Product[]> => {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      // Use the existing products endpoint with search parameter
      const url = `${PRODUCTS_PATH}/?search=${encodeURIComponent(query)}&limit=${limit}`;
      const response = await axiosInstance.get(url);

      // Handle paginated response format
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return response.data.results;
      }

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError(
        'Failed to search products',
        {
          component: 'productService',
          action: 'searchProducts',
          metadata: { query, limit, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Get explicit product relations
  getExplicitRelations: async (productId: number): Promise<ProductRelation[]> => {
    try {
      const url = `${PRODUCTS_API_URL}/${productId}/explicit-relations/`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      logError(
        'Failed to fetch explicit relations',
        {
          component: 'productService',
          action: 'getExplicitRelations',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Update relationship type and notes
  updateRelationship: async (
    productId: number,
    relatedProductId: number,
    updates: {
      relationship_type?: RelationshipType;
      notes?: string;
      is_pinned?: boolean;
    }
  ): Promise<boolean> => {
    try {
      const url = `${PRODUCTS_API_URL}/${productId}/related/${relatedProductId}/`;
      // Include product and related_product fields to match backend serializer expectations
      await axiosInstance.patch(url, {
        ...updates,
        product: productId,
        related_product: relatedProductId,
      });
      return true;
    } catch (error) {
      logError(
        'Failed to update relationship',
        {
          component: 'productService',
          action: 'updateRelationship',
          metadata: { productId, relatedProductId, updates, error },
        },
        { showToast: false, userFacing: false }
      );
      return false;
    }
  },

  // Add multiple products as related
  addMultipleRelatedProducts: async (
    productId: number,
    relatedIds: number[],
    relationship_type: RelationshipType = 'general',
    notes: string = ''
  ): Promise<{
    success: boolean;
    processed: number;
    failed: number;
  }> => {
    if (!productId || !relatedIds.length) {
      return { success: false, processed: 0, failed: 0 };
    }

    try {
      // Instead of using the bulk endpoint, we'll use individual requests
      // to the working endpoint we already have
      let processed = 0;
      let failed = 0;

      // Process each related product ID one by one
      for (const relatedId of relatedIds) {
        try {
          // Add the relationship with the proper relationship type in a single call
          const success = await productService.toggleRelatedProduct(
            productId,
            relatedId,
            false, // Not pinned by default
            relationship_type, // Pass relationship type
            notes // Pass notes
          );

          if (success) {
            processed++;
          } else {
            failed++;
          }
        } catch (err) {
          logError(
            'Failed to add individual related product',
            {
              component: 'productService',
              action: 'addMultipleRelatedProducts',
              metadata: { productId, relatedId, err },
            },
            { showToast: false, userFacing: false }
          );
          failed++;
        }
      }

      return {
        success: processed > 0,
        processed,
        failed,
      };
    } catch (error) {
      logError(
        'Failed to add multiple related products',
        {
          component: 'productService',
          action: 'addMultipleRelatedProducts',
          metadata: { productId, relatedIdsCount: relatedIds.length, error },
        },
        { showToast: false, userFacing: false }
      );
      return { success: false, processed: 0, failed: relatedIds.length };
    }
  },

  // Upload asset (image) for a product
  // If setPrimary is true, backend will store the asset with is_primary = true.
  uploadAsset: async (
    productId: number,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    setPrimary: boolean = false
  ): Promise<ProductAsset> => {
    const url = `${PRODUCTS_API_URL}/${productId}/assets/`;

    // Create FormData object for multipart upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name); // Add name explicitly to match backend expectation
    formData.append('file_size', file.size.toString()); // Send file size to backend
    formData.append('content_type', file.type);

    // Mark as primary if requested (used when product had no previous images)
    if (setPrimary) {
      formData.append('is_primary', 'true');
    }

    /* ------------------------------------------------------------------ *
     * infer a short asset_type label; keep it ≤20 chars (serializer limit)
     * ------------------------------------------------------------------ */
    const mime = file.type.toLowerCase();
    let assetType: string = 'other';
    if (mime.startsWith('image/')) assetType = 'image';
    else if (mime.includes('pdf')) assetType = 'pdf';
    else if (mime.match(/(xlsx|xls|csv|spreadsheet|excel)/)) assetType = 'spreadsheet';
    else if (mime.match(/(doc|docx|text|word)/)) assetType = 'document';

    formData.append('asset_type', assetType);

    try {
      // Let Axios set the correct boundary in the Content-Type header automatically
      const response = await axiosInstance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      if (response.status !== 201 && response.status !== 200) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      const data = response.data;

      // Ensure URL is absolute
      let fileUrl = data.file || '';
      if (fileUrl && !fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
        fileUrl = `http://localhost:8000${fileUrl}`;
      }

      // Clear cache to force refresh
      localStorage.removeItem(`product_assets_${productId}`);

      return {
        id: data.id,
        name: data.name || file.name,
        type: data.asset_type || assetType,
        url: fileUrl,
        file: data.file,
        size: data.file_size || file.size.toString(),
        uploaded_by: data.uploaded_by_name || 'You',
        uploaded_at: data.uploaded_at || new Date().toISOString(),
        is_primary: data.is_primary || false,
        order: data.order || 0,
        content_type: getContentType(file),
      };
    } catch (error) {
      logError(
        'Failed to upload asset file',
        {
          component: 'productService',
          action: 'uploadAsset',
          metadata: {
            productId,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            error,
          },
        },
        { showToast: false, userFacing: false }
      );

      // Enhanced error handling with specific messages
      if (axios.isAxiosError(error)) {
        const response = error.response;

        // Handle specific error cases
        if (response?.status === 400) {
          const errorMsg =
            typeof response.data === 'object'
              ? response.data.file || response.data.error || JSON.stringify(response.data)
              : 'Invalid file format';
          throw new Error(`Upload failed: ${errorMsg}`);
        } else if (response?.status === 413) {
          throw new Error('File too large: The server rejected this file due to its size');
        } else if (response?.status === 415) {
          throw new Error('Unsupported file type');
        } else if (response?.status === 401 || response?.status === 403) {
          // Authentication/authorization issue
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          throw new Error('Authentication error: Please log in again');
        }
        // Detect HTML response instead of JSON (often means server error page)
        else if (
          response?.data &&
          typeof response.data === 'string' &&
          response.data.includes('<!DOCTYPE html>')
        ) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          throw new Error('Invalid server response: Session may have expired');
        }
      }
      // Re-throw the error after logging
      throw error;
    }
  },

  // Delete an asset
  deleteAsset: async (productId: number, assetId: number): Promise<void> => {
    const url = `${PRODUCTS_API_URL}/${productId}/assets/${assetId}/`;

    try {
      await axiosInstance.delete(url);

      // Clear cache to force refresh
      localStorage.removeItem(`product_assets_${productId}`);
    } catch (error) {
      logError(
        'Failed to delete asset',
        {
          component: 'productService',
          action: 'deleteAsset',
          metadata: { productId, assetId, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Set an asset as primary - now this is an atomic operation on the backend
  // The backend will update the asset and product fields in a single transaction
  setAssetPrimary: async (productId: number, assetId: number): Promise<boolean> => {
    try {
      logDebug('Setting asset as primary', { productId, assetId });

      // The backend endpoint handles updating both the asset and product fields in one atomic transaction
      const setPrimaryUrl = `${PRODUCTS_API_URL}/${productId}/assets/${assetId}/set_primary/`;
      await axiosInstance.post(setPrimaryUrl);

      logDebug('Successfully set asset as primary', { productId, assetId });

      // Clear cache to force refresh
      localStorage.removeItem(`product_assets_${productId}`);

      return true;
    } catch (error) {
      logError(
        'Failed to set asset as primary',
        {
          component: 'productService',
          action: 'setAssetPrimary',
          metadata: { productId, assetId, error },
        },
        { showToast: false, userFacing: false }
      );

      // Enhanced error handling for the asset.url error
      if (axios.isAxiosError(error) && error.response?.data?.detail) {
        const errorDetail = error.response.data.detail;
        if (errorDetail.includes("'ProductAsset' object has no attribute 'url'")) {
          logWarn('Backend error - asset.url attribute missing', {
            component: 'productService',
            action: 'setAssetPrimary',
            metadata: { productId, assetId, errorType: 'missing_url_attribute' },
          });
        }
      }

      throw error;
    }
  },

  getAttributeSet,
  getAttributeValues,
  updateAttributeValue,
  createAttributeValue,
  logAttributeActivity,
  createAttribute,

  getProductHistory: async (
    productId: number,
    page = 1,
    filters: Record<string, any> = {}
  ): Promise<PaginatedResponse<ProductEvent>> => {
    try {
      // Construct params with page, page_size, and all filter parameters
      const params = {
        page,
        page_size: 20,
        ...filters,
      };

      const res = await axiosInstance.get(`${PRODUCTS_API_URL}/${productId}/history/`, { params });

      // Handle different response formats
      const data = res.data;

      logDebug('Received product history API response', { productId, responseType: typeof data });

      // If response is empty array or object
      if (
        (Array.isArray(data) && data.length === 0) ||
        (typeof data === 'object' && Object.keys(data).length === 0)
      ) {
        logDebug('Empty history response, returning formatted empty data', { productId });
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      }

      // Handle paginated response format (with results property)
      if (data && typeof data === 'object' && 'results' in data) {
        logDebug('Paginated response format detected', {
          productId,
          resultCount: data.results?.length,
        });
        return data;
      }

      // Handle plain array response format (direct array of events)
      if (Array.isArray(data) && data.length > 0) {
        logDebug('Plain array response format detected, converting to paginated format', {
          productId,
          arrayLength: data.length,
        });

        // Convert plain array to paginated response format
        // Since we don't have pagination info from a plain array, we'll simulate it
        const pageSize = params.page_size || 20;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedResults = data.slice(startIndex, endIndex);
        const hasNext = endIndex < data.length;

        return {
          count: data.length,
          next: hasNext ? `page=${page + 1}` : null,
          previous: page > 1 ? `page=${page - 1}` : null,
          results: paginatedResults,
        };
      }

      // Fallback for truly unexpected formats
      logWarn('Unexpected response format from history API', {
        component: 'productService',
        action: 'getProductHistory',
        metadata: { productId, responseType: typeof data },
      });
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
    } catch (error) {
      logError(
        'Failed to fetch product history',
        {
          component: 'productService',
          action: 'getProductHistory',
          metadata: { productId, page, filters, error },
        },
        { showToast: false, userFacing: false }
      );

      // Log more details about the error if available
      if (axios.isAxiosError(error)) {
        logError(
          'API error in getProductHistory',
          {
            component: 'productService',
            action: 'getProductHistory',
            metadata: {
              productId,
              page,
              filters,
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              message: error.message,
            },
          },
          { showToast: false, userFacing: false }
        );
      }

      // Return empty paginated response on error
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
    }
  },

  // Bulk operation: Update status for multiple products
  bulkSetStatus: async (ids: number[], isActive: boolean): Promise<void> => {
    try {
      const url = `${PRODUCTS_API_URL}/bulk_update/`;
      await axiosInstance.post(url, {
        ids,
        field: 'is_active',
        value: isActive,
      });
    } catch (error) {
      logError(
        'Failed to bulk update product status',
        {
          component: 'productService',
          action: 'bulkSetStatus',
          metadata: { ids, isActive, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk operation: Delete multiple products
  bulkDelete: async (ids: number[]): Promise<void> => {
    try {
      const url = `${PRODUCTS_API_URL}/bulk_delete/`;
      await axiosInstance.post(url, { ids });
    } catch (error) {
      logError(
        'Failed to bulk delete products',
        {
          component: 'productService',
          action: 'bulkDelete',
          metadata: { ids, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk operation: Assign category to multiple products
  bulkAssignCategory: async (ids: number[], category: number | null): Promise<void> => {
    try {
      const url = `${PRODUCTS_API_URL}/bulk_update/`;
      await axiosInstance.post(url, { ids, field: 'category', category });
    } catch (error) {
      logError(
        'Failed to bulk assign category',
        {
          component: 'productService',
          action: 'bulkAssignCategory',
          metadata: { ids, category, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Alias for bulkAssignCategory for backward compatibility
  bulkUpdateCategory: async (ids: number[], category: string): Promise<void> => {
    return productService.bulkAssignCategory(ids, category);
  },

  // Bulk operation: Add tags to multiple products
  bulkAddTags: async (ids: number[], tags: string[]): Promise<void> => {
    try {
      logDebug('Starting bulk add tags operation', { ids, tags });

      const url = `${PRODUCTS_API_URL}/bulk_update/`;
      const payload = { ids, field: 'tags', tags };

      const response = await axiosInstance.post(url, payload);

      if (response.status !== 200) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      logDebug('Bulk add tags completed successfully', { ids, tags });
    } catch (error) {
      logError(
        'Failed to bulk add tags',
        {
          component: 'productService',
          action: 'bulkAddTags',
          metadata: {
            ids,
            tags,
            error,
            status: axios.isAxiosError(error) ? error.response?.status : undefined,
            responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
          },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk operation: Remove tags from multiple products
  bulkRemoveTags: async (ids: number[], tags: string[]): Promise<void> => {
    try {
      logDebug('Starting bulk remove tags operation', { ids, tags });

      const url = `${PRODUCTS_API_URL}/bulk_update/`;
      const payload = { ids, field: 'tags', tags, operation: 'remove' };

      const response = await axiosInstance.post(url, payload);

      if (response.status !== 200) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      logDebug('Bulk remove tags completed successfully', { ids, tags });
    } catch (error) {
      logError(
        'Failed to bulk remove tags',
        {
          component: 'productService',
          action: 'bulkRemoveTags',
          metadata: {
            ids,
            tags,
            error,
            status: axios.isAxiosError(error) ? error.response?.status : undefined,
            responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
          },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk operation: Archive multiple products (set is_archived to true)
  bulkArchive: async (ids: number[]): Promise<void> => {
    try {
      logDebug('Starting bulk archive operation', { ids });

      const url = `${PRODUCTS_API_URL}/bulk_update/`;
      const payload = { ids, field: 'is_archived', value: true };

      const response = await axiosInstance.post(url, payload);

      if (response.status !== 200) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      logDebug('Products archived successfully via bulk endpoint', { ids });
    } catch (error) {
      logWarn('Bulk archive endpoint failed, trying individual updates', {
        component: 'productService',
        action: 'bulkArchive',
        metadata: { ids, error },
      });

      // Fallback: Try individual product updates
      try {
        logDebug('Using fallback: individual product updates', { ids });
        await Promise.all(ids.map(id => productService.updateProduct(id, { is_archived: true })));
        logDebug('Products archived successfully via individual updates', { ids });
      } catch (fallbackError) {
        logError(
          'Both bulk and individual archive updates failed',
          {
            component: 'productService',
            action: 'bulkArchive',
            metadata: { ids, originalError: error, fallbackError },
          },
          { showToast: false, userFacing: false }
        );
        throw fallbackError;
      }
    }
  },

  // Get product suggestions for autocomplete
  suggestProducts: async (q: string): Promise<{ sku?: string; brand?: string; id?: number }[]> => {
    if (!q || q.length < 2) return [];

    try {
      const url = `${PRODUCTS_API_URL}/suggestions/`;
      const response = await axiosInstance.get(url, { params: { q } });

      // If backend not ready, provide a placeholder response
      // Replace with actual response parsing once backend is ready
      if (!response.data || !Array.isArray(response.data)) {
        logWarn('Backend not ready or unexpected response format for suggestions', {
          component: 'productService',
          action: 'suggestProducts',
          metadata: { query: q, responseType: typeof response.data },
        });

        // Temporary placeholder with 1s delay for testing UI
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Return mock data for now
        if (q.toLowerCase().includes('eax')) {
          return [{ sku: 'D15332439', id: 1 }, { sku: 'D14691291', id: 2 }, { brand: 'Ecco' }];
        }
        return [];
      }

      return response.data;
    } catch (error) {
      logError(
        'Failed to get product suggestions',
        {
          component: 'productService',
          action: 'suggestProducts',
          metadata: { query: q, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  getProductCompleteness,

  // Get prices for a product
  getPrices: async (productId: number): Promise<ProductPrice[]> => {
    const url = `${PRODUCTS_PATH}/${productId}/prices/`;
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      logError(
        'Failed to fetch prices',
        {
          component: 'productService',
          action: 'getPrices',
          metadata: { productId, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Add a new price for a product
  addPrice: async (productId: number, priceData: AddPricePayload): Promise<ProductPrice | null> => {
    try {
      // Inject organization defaults if fields are missing (belt-and-suspenders approach)
      const enhancedPriceData = await injectOrganizationDefaults(priceData);

      const url = `${PRODUCTS_API_URL}/${productId}/prices/`;
      const response = await axiosInstance.post(url, enhancedPriceData);
      return response.data;
    } catch (error) {
      logError(
        'Failed to add product price',
        {
          component: 'productService',
          action: 'addPrice',
          metadata: { productId, priceData, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Update an existing price
  patchPrice: async (
    productId: number,
    priceId: number,
    priceData: Partial<ProductPrice>
  ): Promise<ProductPrice | null> => {
    try {
      // For updates, only inject defaults if fields are explicitly being cleared
      const enhancedPriceData = await injectOrganizationDefaultsForUpdate(priceData);

      const url = `${PRODUCTS_API_URL}/${productId}/prices/${priceId}/`;
      const response = await axiosInstance.patch(url, enhancedPriceData);
      return response.data;
    } catch (error) {
      logError(
        'Failed to update product price',
        {
          component: 'productService',
          action: 'patchPrice',
          metadata: { productId, priceId, priceData, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Delete a price
  deletePrice: async (productId: number, priceId: number): Promise<boolean> => {
    try {
      const url = `${PRODUCTS_API_URL}/${productId}/prices/${priceId}/`;
      await axiosInstance.delete(url);
      return true;
    } catch (error) {
      logError(
        'Failed to delete product price',
        {
          component: 'productService',
          action: 'deletePrice',
          metadata: { productId, priceId, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Get all price types
  getPriceTypes: async (): Promise<{ id: number; code: string; label: string }[]> => {
    try {
      const { data } = await axiosInstance.get('/api/price-types/');
      return data;
    } catch (error) {
      logError(
        'Failed to fetch price types',
        {
          component: 'productService',
          action: 'getPriceTypes',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Get all currencies
  getCurrencies: async (): Promise<
    { iso_code: string; symbol: string; name: string; decimals: number }[]
  > => {
    try {
      const { data } = await axiosInstance.get('/api/currencies/');
      return data;
    } catch (error) {
      logError(
        'Failed to fetch currencies',
        {
          component: 'productService',
          action: 'getCurrencies',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Get all sales channels
  getSalesChannels: async (): Promise<{ id: number; name: string }[]> => {
    try {
      const { data } = await axiosInstance.get(paths.channels.root());
      return data;
    } catch (error) {
      logError(
        'Failed to fetch sales channels',
        {
          component: 'productService',
          action: 'getSalesChannels',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Asset Bundles
  getAssetBundles: async (productId: number): Promise<AssetBundle[]> => {
    const url = `${PRODUCTS_API_URL}/${productId}/asset-bundles/`;
    const response = await axiosInstance.get(url);
    return response.data;
  },
  createAssetBundle: async (
    productId: number,
    name: string,
    assetIds: number[]
  ): Promise<AssetBundle> => {
    const url = `${PRODUCTS_API_URL}/${productId}/asset-bundles/`;
    const response = await axiosInstance.post(url, {
      name,
      asset_ids: assetIds,
    });
    return response.data;
  },
  downloadAssetBundle: (productId: number, bundleId: number): void => {
    const url = `${PRODUCTS_API_URL}/${productId}/asset-bundles/${bundleId}/download/`;
    window.open(url, '_blank');
  },

  // Update an asset with new properties
  updateAsset: async (
    productId: number,
    assetId: number,
    data: Partial<ProductAsset>
  ): Promise<ProductAsset> => {
    const url = `${PRODUCTS_API_URL}/${productId}/assets/${assetId}/`;
    const response = await axiosInstance.patch(url, data);
    return response.data;
  },

  // Update tags for an asset
  updateAssetTags: async (
    productId: number,
    assetId: number,
    tags: string[]
  ): Promise<ProductAsset> => {
    logDebug('Starting updateAssetTags operation', {
      productId,
      assetId,
      tagsCount: Array.isArray(tags) ? tags.length : 0,
    });

    try {
      // Safety check for valid inputs
      if (!productId || !assetId) {
        logError(
          'Missing required parameters for updateAssetTags',
          {
            component: 'productService',
            action: 'updateAssetTags',
            metadata: { productId, assetId },
          },
          { showToast: false, userFacing: false }
        );
        throw new Error('Missing productId or assetId');
      }

      // Ensure tags is an array of strings and filter out any invalid values
      const validTags = Array.isArray(tags)
        ? tags.filter(tag => tag && typeof tag === 'string')
        : [];

      logDebug('Updating asset tags', { productId, assetId, validTagsCount: validTags.length });

      // Create API payload with explicit tags field
      const asset = await productService
        .getProductAssets(productId)
        .then(assets => assets.find(a => a.id === assetId));
      const payload = {
        tags: validTags,
        content_type: asset?.content_type || asset?.type || '',
      };

      const url = `${PRODUCTS_API_URL}/${productId}/assets/${assetId}/`;
      const response = await axiosInstance.patch(url, payload);

      if (!response.data) {
        logError(
          'Received empty response from server when updating asset tags',
          {
            component: 'productService',
            action: 'updateAssetTags',
            metadata: { productId, assetId },
          },
          { showToast: false, userFacing: false }
        );
        throw new Error('Empty response from server');
      }

      // Clear cache to force refresh
      localStorage.removeItem(`product_assets_${productId}`);

      // Handle various response formats
      if (response.data.tags) {
        logDebug('Asset tags updated successfully', {
          productId,
          assetId,
          tags: response.data.tags,
        });
      } else {
        logWarn('Response does not contain tags field after updating asset tags', {
          component: 'productService',
          action: 'updateAssetTags',
          metadata: { productId, assetId },
        });
      }

      return response.data;
    } catch (error) {
      logError(
        'Failed to update asset tags',
        {
          component: 'productService',
          action: 'updateAssetTags',
          metadata: { productId, assetId, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk archive assets
  bulkArchiveAssets: async (productId: number, assets: ProductAsset[]): Promise<void> => {
    const url = `${PRODUCTS_API_URL}/${productId}/assets/bulk-update/`;
    const asset_ids = assets.map(a => a.id);
    await axiosInstance.post(url, {
      asset_ids,
      is_archived: true,
    });
  },

  // Delete an asset bundle
  deleteAssetBundle: async (productId: number, bundleId: number): Promise<void> => {
    const url = `${PRODUCTS_API_URL}/${productId}/asset-bundles/${bundleId}/`;
    await axiosInstance.delete(url);
  },

  // Get product assets with on-demand loading
  getProductAssetsOnDemand: async (
    productId: number,
    options: { includeAssets?: boolean } = {}
  ): Promise<ProductAsset[]> => {
    if (!options.includeAssets) return [];

    try {
      const url = `${PRODUCTS_PATH}/${productId}/?fields=assets`;
      const response: AxiosResponse<{ assets?: ProductAsset[] }> = await axiosInstance.get(url);
      return response.data.assets || [];
    } catch (error) {
      logError(
        'Failed to fetch product assets on demand',
        {
          component: 'productService',
          action: 'getProductAssetsOnDemand',
          metadata: { productId, options, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  // Download product overview as PDF
  downloadProductPdf: async (productId: number, productName: string): Promise<void> => {
    try {
      // Get current language from i18n
      const currentLanguage =
        localStorage.getItem('kernlogic-language') || localStorage.getItem('i18nextLng') || 'en';

      const url = `${PRODUCTS_API_URL}/${productId}/pdf/?lang=${currentLanguage}`;
      const response = await axiosInstance.get(url, {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      // Generate filename with product name and current date
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const sanitizedProductName = productName.replace(/[^a-zA-Z0-9\s-_]/g, '').trim();
      const filename = `${sanitizedProductName}_${currentDate}.pdf`;

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      logError(
        'Failed to download product PDF',
        {
          component: 'productService',
          action: 'downloadProductPdf',
          metadata: { productId, productName, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Get effective attributes for a product (family inheritance + overrides)
  getEffectiveAttributes: async (
    productId: number,
    locale?: string,
    channel?: string
  ): Promise<EffectiveAttributeGroup[]> => {
    try {
      const params = new URLSearchParams();
      if (locale) params.append('locale', locale);
      if (channel) params.append('channel', channel);

      const url = `${PRODUCTS_API_URL}/${productId}/effective-attributes/${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      logError(
        'Failed to fetch effective attributes',
        {
          component: 'productService',
          action: 'getEffectiveAttributes',
          metadata: { productId, locale, channel, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Bulk fetch products with their effective attributes (optimized for exports)
  bulkGetProductsWithAttributes: async (
    productIds: number[],
    options: {
      locale?: string;
      channel?: string;
      batchSize?: number;
      onProgress?: (completed: number, total: number) => void;
    } = {}
  ): Promise<
    Array<{ product: Product; effectiveAttributes: EffectiveAttributeGroup[] } | null>
  > => {
    const { locale, channel, batchSize = 8, onProgress } = options;

    logDebug('Starting bulk fetch of products with attributes', {
      component: 'productService',
      action: 'bulkGetProductsWithAttributes',
      metadata: {
        productCount: productIds.length,
        batchSize,
        locale,
        channel,
      },
    });

    try {
      // Split product IDs into batches to avoid overwhelming the server
      const batches: number[][] = [];
      for (let i = 0; i < productIds.length; i += batchSize) {
        batches.push(productIds.slice(i, i + batchSize));
      }

      const results: Array<{
        product: Product;
        effectiveAttributes: EffectiveAttributeGroup[];
      } | null> = [];
      let completedCount = 0;

      // Process batches sequentially to control server load
      for (const batch of batches) {
        // Within each batch, process products concurrently
        const batchPromises = batch.map(
          async (
            productId
          ): Promise<{
            product: Product;
            effectiveAttributes: EffectiveAttributeGroup[];
          } | null> => {
            try {
              // Fetch product and attributes in parallel for this specific product
              const [product, effectiveAttrs] = await Promise.all([
                productService.getProduct(productId),
                productService.getEffectiveAttributes(productId, locale, channel),
              ]);

              return { product, effectiveAttributes: effectiveAttrs };
            } catch (error) {
              logError(
                'Failed to fetch product with attributes in bulk operation',
                {
                  component: 'productService',
                  action: 'bulkGetProductsWithAttributes',
                  metadata: { productId, error },
                },
                { showToast: false, userFacing: false }
              );

              // Try fallback to just product data
              try {
                const product = await productService.getProduct(productId);
                return { product, effectiveAttributes: [] };
              } catch {
                return null; // Complete failure for this product
              }
            }
          }
        );

        // Wait for the current batch to complete
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);

        completedCount += batch.length;

        // Report progress if callback provided
        if (onProgress) {
          onProgress(completedCount, productIds.length);
        }

        // Small delay between batches to be gentle on the server
        if (batches.indexOf(batch) < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      logDebug('Bulk fetch completed', {
        component: 'productService',
        action: 'bulkGetProductsWithAttributes',
        metadata: {
          requested: productIds.length,
          successful: results.filter(r => r !== null).length,
          failed: results.filter(r => r === null).length,
        },
      });

      return results;
    } catch (error) {
      logError(
        'Bulk fetch of products with attributes failed',
        {
          component: 'productService',
          action: 'bulkGetProductsWithAttributes',
          metadata: { productIds, error },
        },
        { showToast: false, userFacing: false }
      );
      throw error;
    }
  },

  // Comment-related methods

  // Get comments for a product
  getComments: async (
    productId: number,
    options: {
      flat?: boolean;
      parent_id?: number | 'null';
      search?: string;
      page?: number;
      page_size?: number;
      ordering?: string;
    } = {}
  ): Promise<CommentListResponse> => {
    try {
      const params = new URLSearchParams();

      if (options.flat) params.append('flat', 'true');
      if (options.parent_id !== undefined) {
        params.append('parent_id', options.parent_id === null ? 'null' : String(options.parent_id));
      }
      if (options.search) params.append('search', options.search);
      if (options.page) params.append('page', String(options.page));
      if (options.page_size) params.append('page_size', String(options.page_size));
      if (options.ordering) params.append('ordering', options.ordering);

      const queryString = params.toString();
      const url = `/api/products/${productId}/comments/${queryString ? `?${queryString}` : ''}`;

      const response = await axiosInstance.get(url);

      if (isHtmlResponse(response.data)) {
        throw new Error(
          'Received HTML response instead of JSON. Please check your authentication.'
        );
      }

      return response.data;
    } catch (error: any) {
      logError(
        'Failed to fetch comments',
        {
          component: 'productService',
          action: 'getComments',
          metadata: { productId, options, error },
        },
        { showToast: false, userFacing: false }
      );
      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Failed to fetch comments'
      );
    }
  },

  // Get a specific comment
  getComment: async (productId: number, commentId: number): Promise<Comment> => {
    try {
      const response = await axiosInstance.get(`/api/products/${productId}/comments/${commentId}/`);

      if (isHtmlResponse(response.data)) {
        throw new Error(
          'Received HTML response instead of JSON. Please check your authentication.'
        );
      }

      return response.data;
    } catch (error: any) {
      logError(
        'Failed to fetch comment',
        {
          component: 'productService',
          action: 'getComment',
          metadata: { productId, commentId, error },
        },
        { showToast: false, userFacing: false }
      );
      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Failed to fetch comment'
      );
    }
  },

  // Create a new comment
  createComment: async (productId: number, payload: CreateCommentPayload): Promise<Comment> => {
    try {
      // Validate payload
      if (!payload.body || !payload.body.trim()) {
        throw new Error('Comment body is required');
      }

      if (payload.body.length > 5000) {
        throw new Error('Comment body cannot exceed 5000 characters');
      }

      const response = await axiosInstance.post(`/api/products/${productId}/comments/`, {
        body: payload.body.trim(),
        parent: payload.parent || null,
      });

      if (isHtmlResponse(response.data)) {
        throw new Error(
          'Received HTML response instead of JSON. Please check your authentication.'
        );
      }

      return response.data;
    } catch (error: any) {
      logError(
        'Failed to create comment',
        {
          component: 'productService',
          action: 'createComment',
          metadata: { productId, payload, error },
        },
        { showToast: false, userFacing: false }
      );

      // Handle validation errors
      if (error.response?.data?.body) {
        throw new Error(error.response.data.body[0] || 'Invalid comment body');
      }

      if (error.response?.data?.parent) {
        throw new Error(error.response.data.parent[0] || 'Invalid parent comment');
      }

      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Failed to create comment'
      );
    }
  },

  // Update an existing comment
  updateComment: async (
    productId: number,
    commentId: number,
    payload: UpdateCommentPayload
  ): Promise<Comment> => {
    try {
      // Validate payload
      if (!payload.body || !payload.body.trim()) {
        throw new Error('Comment body is required');
      }

      if (payload.body.length > 5000) {
        throw new Error('Comment body cannot exceed 5000 characters');
      }

      const response = await axiosInstance.patch(
        `/api/products/${productId}/comments/${commentId}/`,
        {
          body: payload.body.trim(),
        }
      );

      if (isHtmlResponse(response.data)) {
        throw new Error(
          'Received HTML response instead of JSON. Please check your authentication.'
        );
      }

      return response.data;
    } catch (error: any) {
      logError(
        'Failed to update comment',
        {
          component: 'productService',
          action: 'updateComment',
          metadata: { productId, commentId, payload, error },
        },
        { showToast: false, userFacing: false }
      );

      // Handle specific error cases
      if (error.response?.status === 403) {
        throw new Error('You can only edit your own comments');
      }

      if (error.response?.status === 404) {
        throw new Error('Comment not found');
      }

      // Handle validation errors
      if (error.response?.data?.body) {
        throw new Error(error.response.data.body[0] || 'Invalid comment body');
      }

      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Failed to update comment'
      );
    }
  },

  // Delete a comment
  deleteComment: async (productId: number, commentId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`/api/products/${productId}/comments/${commentId}/`);
    } catch (error: any) {
      logError(
        'Failed to delete comment',
        {
          component: 'productService',
          action: 'deleteComment',
          metadata: { productId, commentId, error },
        },
        { showToast: false, userFacing: false }
      );

      // Handle specific error cases
      if (error.response?.status === 403) {
        throw new Error('You can only delete your own comments');
      }

      if (error.response?.status === 404) {
        throw new Error('Comment not found');
      }

      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          'Failed to delete comment'
      );
    }
  },
};
